import { useState, useEffect, lazy, Suspense } from "react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Users, BarChart3, Settings, QrCode, TrendingUp, Calendar, Activity, User, LogOut, Database, Search, Upload, ChevronDown, UserCircle, Mail, FileText, Stethoscope, Menu, Bell, Send } from "lucide-react";

// Lazy load heavy components
const UserManagement = lazy(() => import("@/components/user-management"));
const SessionsManagement = lazy(() => import("@/components/sessions-management"));
const KnowledgeBaseSearch = lazy(() => import("@/components/knowledge-base-search"));
const KnowledgeBaseUpload = lazy(() => import("@/components/knowledge-base-upload"));
const KnowledgeBaseManagement = lazy(() => import("@/components/knowledge-base-management"));
const KnowledgeBaseTestChat = lazy(() => import("@/components/knowledge-base-test-chat"));
const KnowledgeUsageAnalytics = lazy(() => import("@/components/knowledge-usage-analytics"));
const QRAnalytics = lazy(() => import("@/components/qr-analytics"));
const QRManagement = lazy(() => import("@/components/qr-management"));
// Import QRHistory component
const QRHistory = lazy(() => import("@/components/qr-history"));
// Import EmailManagement and EmailTemplates components
const EmailManagement = lazy(() => import("@/components/email-management"));
const EmailTemplates = lazy(() => import("@/components/email-templates"));
const AdminNotifications = lazy(() => import("@/components/admin-notifications"));
// Import AddTherapist component directly (not lazy loaded to avoid module errors)
import AddTherapist from "@/pages/add-therapist";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarProvider,
  SidebarInset,
  useSidebar
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

interface DashboardAnalytics {
  totalUsers: number;
  totalSessions: number;
  totalBookings: number;
  weeklyActivity: number;
  timestamp: string;
  totalAdmins: number;
  totalTherapists: number;
  totalClients: number;
  aiSessions: number;
  humanSessions: number;
  groupSessions: number;
}

// Update AdminView type to include user type filters and other views
type AdminView = 'users-all' | 'users-admins' | 'users-therapists' | 'users-clients' | 'sessions' | 'knowledge-search' | 'knowledge-upload' | 'knowledge-manage' | 'knowledge-test-chat' | 'knowledge-usage' | 'qr-analytics' | 'qr-management' | 'qr-history' | 'analytics' | 'profile' | 'email-service' | 'email-templates' | 'add-admin' | 'add-therapist' | 'notif';

// Placeholder for a loading fallback component
const LoadingFallback = () => (
  <div className="text-center py-12">
    <div className="w-8 h-8 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
    <p className="text-gray-600">Loading...</p>
  </div>
);

function MobileMenuButton() {
  const { toggleSidebar } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="md:hidden h-9 w-9"
      onClick={toggleSidebar}
    >
      <Menu className="h-5 w-5" />
      <span className="sr-only">Toggle menu</span>
    </Button>
  );
}

function SidebarNavContent({ children }: { children: React.ReactNode }) {
  const { isMobile, setOpenMobile } = useSidebar();
  const handleClick = (e: React.MouseEvent) => {
    if (!isMobile) return;
    const target = e.target as HTMLElement;
    const menuButton = target.closest('[data-sidebar="menu-button"]');
    if (menuButton && (menuButton.hasAttribute('data-state'))) return;
    const isNavItem = target.closest('[data-sidebar="menu-button"], [data-sidebar="menu-sub-button"]');
    if (isNavItem) {
      setTimeout(() => setOpenMobile(false), 150);
    }
  };
  return <div onClick={handleClick}>{children}</div>;
}

export default function AdminConsole() {
  const { user, isAuthenticated, logout } = useAuth();
  const { toast } = useToast();
  const [location, setLocation] = useLocation();
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [loading, setLoading] = useState(false);

  // Parse URL query parameters for initial state
  const urlParams = new URLSearchParams(window.location.search);
  const initialView = (urlParams.get('view') || 'analytics') as AdminView;

  const [activeView, setActiveView] = useState<AdminView>(initialView);

  // Sync activeView with URL changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const viewParam = params.get('view');
    if (viewParam) {
      setActiveView(viewParam as AdminView);
    }
  }, [location]);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        title: "Password Mismatch",
        description: "New password and confirmation don't match.",
        variant: "destructive"
      });
      return;
    }

    if (newPassword.length < 8) {
      toast({
        title: "Password Too Short",
        description: "Password must be at least 8 characters long.",
        variant: "destructive"
      });
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentPassword,
          newPassword
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to change password');
      }

      toast({
        title: "Password Changed",
        description: "Your password has been successfully updated.",
      });

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to change password",
        variant: "destructive"
      });
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');

      if (!token) {
        toast({
          title: "Authentication Required",
          description: "Please log in to view analytics",
          variant: "destructive"
        });
        return;
      }

      const response = await fetch('/api/analytics/dashboard', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 401 || response.status === 403) {
        toast({
          title: "Authentication Failed",
          description: "Your session has expired. Please log in again.",
          variant: "destructive"
        });
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (error: any) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to load analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated && user?.role === 'admin') {
      fetchAnalytics();
    }
  }, [isAuthenticated, user]);

  // Redirect to admin login if not authenticated or not admin
  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      window.location.href = '/admin-login';
    }
  }, [isAuthenticated, user]);

  // Clear URL params after initial load to keep URL clean
  useEffect(() => {
    if (urlParams.has('view')) {
      window.history.replaceState({}, '', '/admin');
    }
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/admin-login';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (!isAuthenticated || user?.role !== 'admin') {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
            <p className="text-gray-600">Redirecting to admin login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const navigationItems = [
    { id: 'sessions' as AdminView, label: 'Sessions', icon: Activity, path: '/admin?view=sessions' },
    { id: 'analytics' as AdminView, label: 'Analytics', icon: TrendingUp, path: '/admin?view=analytics' },
    { id: 'profile' as AdminView, label: 'Profile', icon: User, path: '/admin?view=profile' },
  ];

  // Handler for changing the active view and navigating
  const handleViewChange = (view: AdminView) => {
    setActiveView(view);
    setLocation(`/admin?view=${view}`);
  };

  return (
    <SidebarProvider defaultOpen={true}>
      <div className="flex min-h-screen w-full bg-gray-50 flex-col">
        <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 md:px-6 py-4">
            <div className="flex items-center gap-3">
              <MobileMenuButton />
              <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleViewChange('analytics')}>
                <div className="w-10 h-10 flex items-center justify-center">
                  <img src="/owl-logo.png" alt="HealthyOwl Logo" className="h-10 w-10 object-contain" />
                </div>
                <div className="hidden sm:block">
                  <h1 className="text-xl font-bold text-gray-900">Healthy Owl</h1>
                  <p className="text-xs text-gray-500">Admin Console</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="flex flex-1 relative">
          {/* Left Sidebar */}
          <Sidebar collapsible="icon" className="border-r border-gray-200 absolute left-0 top-0 bottom-0 h-full">
            <SidebarHeader className="border-b border-gray-200 px-3 py-3 gap-0">
              <div className="w-8 h-8 bg-sage-600 rounded-lg flex items-center justify-center group-data-[collapsible=icon]:mx-auto">
                <Shield className="w-5 h-5 text-white" />
              </div>
            </SidebarHeader>

            <SidebarContent>
              <SidebarNavContent>
              <SidebarMenu>
                {/* Analytics/Dashboard - First Menu Item */}
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => handleViewChange('analytics')}
                    isActive={activeView === 'analytics'}
                    tooltip="Analytics"
                    className={cn(
                      "w-full",
                      activeView === 'analytics' && "bg-sage-50 text-sage-700"
                    )}
                  >
                    <TrendingUp className={cn("w-5 h-5", activeView === 'analytics' ? "text-sage-600" : "text-gray-400")} />
                    <span>Analytics</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>

                {/* Add Users Menu */}
                <Collapsible defaultOpen={false} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Add Users" className="cursor-pointer">
                        <UserCircle className="w-5 h-5 text-gray-400" />
                        <span>Add Users</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('add-admin')}
                            isActive={activeView === 'add-admin'}
                            className="cursor-pointer"
                          >
                            <Shield className="w-4 h-4" />
                            <span>Add Admin</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('add-therapist')}
                            isActive={activeView === 'add-therapist'}
                            className="cursor-pointer"
                          >
                            <Stethoscope className="w-4 h-4" />
                            <span>Add Therapist</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* User Management with Submenu */}
                <Collapsible defaultOpen={true} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="User Management" className="cursor-pointer">
                        <Users className="w-5 h-5 text-gray-400" />
                        <span>User Management</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('users-admins')}
                            isActive={activeView === 'users-admins'}
                            className="cursor-pointer"
                          >
                            <Shield className="w-4 h-4" />
                            <span>Admins</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('users-therapists')}
                            isActive={activeView === 'users-therapists'}
                            className="cursor-pointer"
                          >
                            <Stethoscope className="w-4 h-4" />
                            <span>Therapists</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('users-clients')}
                            isActive={activeView === 'users-clients'}
                            className="cursor-pointer"
                          >
                            <User className="w-4 h-4" />
                            <span>Clients</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* Sessions and Profile */}
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeView === item.id;

                  return (
                    <SidebarMenuItem key={item.id}>
                      <SidebarMenuButton
                        onClick={() => handleViewChange(item.id)}
                        isActive={isActive}
                        tooltip={item.label}
                        className={cn(
                          "w-full",
                          isActive && "bg-sage-50 text-sage-700"
                        )}
                      >
                        <Icon className={cn("w-5 h-5", isActive ? "text-sage-600" : "text-gray-400")} />
                        <span>{item.label}</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}

                {/* QR Code with Submenu */}
                <Collapsible defaultOpen={false} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="QR Code" className="cursor-pointer">
                        <QrCode className="w-5 h-5 text-gray-400" />
                        <span>QR Code</span>
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('qr-analytics')}
                            isActive={activeView === 'qr-analytics'}
                            className="cursor-pointer"
                          >
                            <BarChart3 className="w-4 h-4" />
                            <span>Analytics</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('qr-management')}
                            isActive={activeView === 'qr-management'}
                            className="cursor-pointer"
                          >
                            <QrCode className="w-4 h-4" />
                            <span>Management</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        {/* New QR History Submenu Item */}
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('qr-history')}
                            isActive={activeView === 'qr-history'}
                            className="cursor-pointer"
                          >
                            <Calendar className="w-4 h-4" />
                            <span>History</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* Knowledge Base with Submenu */}
                <Collapsible defaultOpen={false} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Knowledge Base" className="cursor-pointer">
                        <Database className="w-5 h-5 text-gray-400" />
                        <span>Knowledge Base</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('knowledge-search')}
                            isActive={activeView === 'knowledge-search'}
                            className="cursor-pointer"
                          >
                            <Search className="w-4 h-4" />
                            <span>Search</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('knowledge-upload')}
                            isActive={activeView === 'knowledge-upload'}
                            className="cursor-pointer"
                          >
                            <Upload className="w-4 h-4" />
                            <span>Upload</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('knowledge-manage')}
                            isActive={activeView === 'knowledge-manage'}
                            className="cursor-pointer"
                          >
                            <Database className="w-4 h-4" />
                            <span>Manage Documents</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('knowledge-usage')}
                            isActive={activeView === 'knowledge-usage'}
                            className="cursor-pointer"
                          >
                            <BarChart3 className="w-4 h-4" />
                            <span>Usage Analytics</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('knowledge-test-chat')}
                            isActive={activeView === 'knowledge-test-chat'}
                            className="cursor-pointer"
                          >
                            <Activity className="w-4 h-4" />
                            <span>Test AI Chat</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                {/* Email Service with Submenu */}
                <Collapsible defaultOpen={false} className="group/collapsible">
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton tooltip="Email Service" className="cursor-pointer">
                        <Mail className="w-5 h-5 text-gray-400" />
                        <span>Email Service</span>
                        <ChevronDown className="ml-auto h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('email-service')}
                            isActive={activeView === 'email-service'}
                            className="cursor-pointer"
                          >
                            <Mail className="w-4 h-4" />
                            <span>Logs & Testing</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                        <SidebarMenuSubItem>
                          <SidebarMenuSubButton
                            onClick={() => handleViewChange('email-templates')}
                            isActive={activeView === 'email-templates'}
                            className="cursor-pointer"
                          >
                            <FileText className="w-4 h-4" />
                            <span>Templates</span>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>

                <SidebarMenuItem>
                  <SidebarMenuButton
                    tooltip="Notifications"
                    onClick={() => handleViewChange('notif')}
                    isActive={activeView === 'notif'}
                    className="cursor-pointer"
                  >
                    <Bell className="w-5 h-5 text-gray-400" />
                    <span>Notifications</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
              </SidebarNavContent>
            </SidebarContent>
          </Sidebar>

          {/* Main Content */}
          <SidebarInset>
            <div className="container mx-auto px-6 py-8 max-w-7xl">
              {/* All Users View */}
              {activeView === 'users-all' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">All Users</h1>
                    <p className="text-gray-600 mt-1">View and manage all users across all types</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <UserManagement filterType="all" />
                  </Suspense>
                </div>
              )}

              {/* Admins View */}
              {activeView === 'users-admins' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Admin Users</h1>
                    <p className="text-gray-600 mt-1">Manage administrator accounts</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <UserManagement filterType="admin" />
                  </Suspense>
                </div>
              )}

              {/* Therapists View */}
              {activeView === 'users-therapists' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Therapist Users</h1>
                    <p className="text-gray-600 mt-1">Manage therapist accounts and profiles</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <UserManagement filterType="therapist" />
                  </Suspense>
                </div>
              )}

              {/* Clients View */}
              {activeView === 'users-clients' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Client Users</h1>
                    <p className="text-gray-600 mt-1">Manage client accounts</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <UserManagement filterType="client" />
                  </Suspense>
                </div>
              )}

              {/* Sessions View */}
              {activeView === 'sessions' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Sessions Management</h1>
                    <p className="text-gray-600 mt-1">View and manage all therapy sessions (AI, Human, Group)</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <SessionsManagement />
                  </Suspense>
                </div>
              )}

              {/* Knowledge Base Search View */}
              {activeView === 'knowledge-search' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Knowledge Base Search</h1>
                    <p className="text-gray-600 mt-1">Search the RAG knowledge base for AI-enhanced responses</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <KnowledgeBaseSearch />
                  </Suspense>
                </div>
              )}

              {/* Knowledge Base Upload View */}
              {activeView === 'knowledge-upload' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Knowledge Base Upload</h1>
                    <p className="text-gray-600 mt-1">Upload documents to the RAG knowledge base</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <KnowledgeBaseUpload />
                  </Suspense>
                </div>
              )}

              {/* Knowledge Base Manage View */}
              {activeView === 'knowledge-manage' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Knowledge Base Management</h1>
                    <p className="text-gray-600 mt-1">Manage and organize documents in the RAG knowledge base</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <KnowledgeBaseManagement />
                  </Suspense>
                </div>
              )}

              {/* Knowledge Usage Analytics View */}
              {activeView === 'knowledge-usage' && (
                <div>
                  <Suspense fallback={<LoadingFallback />}>
                    <KnowledgeUsageAnalytics />
                  </Suspense>
                </div>
              )}

              {/* Knowledge Base Test Chat View */}
              {activeView === 'knowledge-test-chat' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Test AI Chat</h1>
                    <p className="text-gray-600 mt-1">Test AI responses with knowledge base context before users see them</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <KnowledgeBaseTestChat />
                  </Suspense>
                </div>
              )}


              {/* QR Analytics View */}
              {activeView === 'qr-analytics' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">QR Code Analytics</h1>
                    <p className="text-gray-600 mt-1">Track QR code scans and campaign performance</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <QRAnalytics />
                  </Suspense>
                </div>
              )}

              {/* QR Management View */}
              {activeView === 'qr-management' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">QR Code Management</h1>
                    <p className="text-gray-600 mt-1">Generate and configure your QR codes</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <QRManagement />
                  </Suspense>
                </div>
              )}

              {/* QR History View */}
              {activeView === 'qr-history' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">QR Code History</h1>
                    <p className="text-gray-600 mt-1">View and manage past QR codes</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <QRHistory />
                  </Suspense>
                </div>
              )}

              {/* Email Management View */}
              {activeView === 'email-service' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Email Service Management</h1>
                    <p className="text-gray-600 mt-1">Configure and monitor email sending</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <EmailManagement />
                  </Suspense>
                </div>
              )}

              {/* Email Templates View */}
              {activeView === 'email-templates' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Email Templates</h1>
                    <p className="text-gray-600 mt-1">Manage reusable email templates</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <EmailTemplates />
                  </Suspense>
                </div>
              )}

              {/* Notifications View */}
              {activeView === 'notif' && (
                <div>
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
                    <p className="text-gray-600 mt-1">Send and manage user notifications</p>
                  </div>
                  <Suspense fallback={<LoadingFallback />}>
                    <AdminNotifications />
                  </Suspense>
                </div>
              )}

              {/* Add Therapist View */}
              {activeView === 'add-therapist' && (
                <div>
                  <AddTherapist />
                </div>
              )}

              {/* Add Admin View */}
              {activeView === 'add-admin' && (
                <div className="space-y-6 pb-8">
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Add Admin User</h1>
                    <p className="text-gray-600 mt-1">Create a new administrator account</p>
                  </div>

                  <Card className="mb-8">
                    <CardHeader>
                      <CardTitle>Admin Details</CardTitle>
                      <CardDescription>Enter the details for the new admin user</CardDescription>
                    </CardHeader>
                    <CardContent className="pb-6">
                      <form onSubmit={async (e) => {
                        e.preventDefault();
                        const formData = new FormData(e.currentTarget);
                        const password = formData.get('password') as string;
                        const confirmPassword = formData.get('confirmPassword') as string;
                        const firstName = formData.get('firstName') as string;
                        const lastName = formData.get('lastName') as string;

                        if (password !== confirmPassword) {
                          toast({
                            title: "Password Mismatch",
                            description: "New password and confirmation don't match.",
                            variant: "destructive"
                          });
                          return;
                        }

                        if (password.length < 8) {
                          toast({
                            title: "Password Too Short",
                            description: "Password must be at least 8 characters long.",
                            variant: "destructive"
                          });
                          return;
                        }

                        if (firstName.trim().length < 2) {
                          toast({
                            title: "Invalid First Name",
                            description: "First name must be at least 2 characters",
                            variant: "destructive"
                          });
                          return;
                        }

                        if (lastName.trim().length < 2) {
                          toast({
                            title: "Invalid Last Name",
                            description: "Last name must be at least 2 characters",
                            variant: "destructive"
                          });
                          return;
                        }

                        try {
                          const token = localStorage.getItem('auth_token');
                          const response = await fetch('/api/auth/register/admin', {
                            method: 'POST',
                            headers: {
                              'Content-Type': 'application/json',
                              'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify({
                              email: formData.get('email'),
                              password: password,
                              firstName: firstName,
                              lastName: lastName,
                              isActive: formData.get('isActive') === 'on',
                              createdBy: user?.id
                            })
                          });

                          const data = await response.json();

                          if (!response.ok) {
                            throw new Error(data.message || 'Failed to create admin user');
                          }

                          toast({
                            title: 'Success',
                            description: `Admin user ${formData.get('email')} created successfully`,
                          });

                          handleViewChange('users-admins');
                        } catch (error: any) {
                          toast({
                            title: 'Error',
                            description: error.message || 'Failed to create admin user',
                            variant: 'destructive'
                          });
                        }
                      }} className="space-y-6">
                        <div className="space-y-2">
                          <label htmlFor="email" className="text-sm font-medium text-gray-700">Email *</label>
                          <Input
                            id="email"
                            name="email"
                            type="email"
                            placeholder="admin@healthyowl.com"
                            required
                            className="w-full"
                          />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <label htmlFor="firstName" className="text-sm font-medium text-gray-700">First Name *</label>
                            <Input
                              id="firstName"
                              name="firstName"
                              placeholder="John"
                              required
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-2">
                            <label htmlFor="lastName" className="text-sm font-medium text-gray-700">Last Name *</label>
                            <Input
                              id="lastName"
                              name="lastName"
                              placeholder="Doe"
                              required
                              className="w-full"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="password" className="text-sm font-medium text-gray-700">Password *</label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            placeholder="At least 8 characters"
                            required
                            className="w-full"
                          />
                          <p className="text-xs text-gray-500">Minimum 8 characters</p>
                        </div>

                        <div className="space-y-2">
                          <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">Confirm Password *</label>
                          <Input
                            id="confirmPassword"
                            name="confirmPassword"
                            type="password"
                            placeholder="Re-enter password"
                            required
                            className="w-full"
                          />
                        </div>

                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="space-y-0.5">
                            <label htmlFor="isActive" className="text-base font-medium text-gray-700">Activate account immediately</label>
                            <p className="text-sm text-gray-500">User will be able to login right away</p>
                          </div>
                          <input
                            id="isActive"
                            name="isActive"
                            type="checkbox"
                            defaultChecked
                            className="w-4 h-4"
                          />
                        </div>

                        <div className="pt-4">
                          <Button
                            type="submit"
                            className="w-full bg-sage-600 hover:bg-sage-700 text-white font-semibold h-12 text-base"
                            disabled={false}
                          >
                            Create Admin User
                          </Button>
                        </div>
                      </form>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Analytics View */}
              {activeView === 'analytics' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900">Analytics Dashboard</h1>
                      <p className="text-gray-600 mt-1">View application analytics and insights</p>
                    </div>
                    <Button onClick={fetchAnalytics} variant="outline" disabled={loading}>
                      {loading ? "Loading..." : "Refresh"}
                    </Button>
                  </div>

                  {loading && !analytics ? (
                    <div className="text-center py-12">
                      <div className="w-8 h-8 border-4 border-sage-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading analytics...</p>
                    </div>
                  ) : analytics ? (
                    <>
                      {/* Stats Overview */}
                      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Active Users</CardTitle>
                            <Users className="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold mb-3">{analytics.totalUsers}</div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Clients:</span>
                                <span className="font-semibold text-green-600">{analytics.totalClients || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Therapists:</span>
                                <span className="font-semibold text-blue-600">{analytics.totalTherapists || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Admins:</span>
                                <span className="font-semibold text-purple-600">{analytics.totalAdmins || 0}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
                            <Activity className="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold mb-3">{analytics.totalSessions}</div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">AI:</span>
                                <span className="font-semibold text-purple-600">{analytics.aiSessions || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Human:</span>
                                <span className="font-semibold text-blue-600">{analytics.humanSessions || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Group:</span>
                                <span className="font-semibold text-teal-600">{analytics.groupSessions || 0}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Total Bookings</CardTitle>
                            <Calendar className="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold mb-3">{analytics.totalBookings}</div>
                            <div className="space-y-1">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">AI:</span>
                                <span className="font-semibold text-purple-600">{analytics.aiSessions || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Human:</span>
                                <span className="font-semibold text-blue-600">{analytics.humanSessions || 0}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-muted-foreground">Group:</span>
                                <span className="font-semibold text-teal-600">{analytics.groupSessions || 0}</span>
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card>
                          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Weekly Activity</CardTitle>
                            <TrendingUp className="h-4 w-4 text-muted-foreground" />
                          </CardHeader>
                          <CardContent>
                            <div className="text-2xl font-bold">{analytics.weeklyActivity}</div>
                            <p className="text-xs text-muted-foreground">Last 7 days</p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Engagement Metrics */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Platform Engagement</CardTitle>
                          <CardDescription>Key performance indicators</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-3">
                              <div>
                                <p className="font-medium">Average Sessions per User</p>
                                <p className="text-sm text-muted-foreground">User engagement metric</p>
                              </div>
                              <div className="text-2xl font-bold">
                                {analytics.totalUsers > 0
                                  ? (analytics.totalSessions / analytics.totalUsers).toFixed(1)
                                  : '0'}
                              </div>
                            </div>

                            <div className="flex items-center justify-between border-b pb-3">
                              <div>
                                <p className="font-medium">Booking Rate</p>
                                <p className="text-sm text-muted-foreground">Users who book sessions</p>
                              </div>
                              <div className="text-2xl font-bold">
                                {analytics.totalUsers > 0
                                  ? ((analytics.totalBookings / analytics.totalUsers) * 100).toFixed(1)
                                  : '0'}%
                              </div>
                            </div>

                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">Weekly Engagement</p>
                                <p className="text-sm text-muted-foreground">Active users this week</p>
                              </div>
                              <div className="text-2xl font-bold">
                                {analytics.totalUsers > 0
                                  ? ((analytics.weeklyActivity / analytics.totalUsers) * 100).toFixed(1)
                                  : '0'}%
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      {/* Quick Links */}
                      <Card>
                        <CardHeader>
                          <CardTitle>Quick Access</CardTitle>
                          <CardDescription>Navigate to detailed analytics</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid gap-3 md:grid-cols-2">
                            <Button variant="outline" asChild className="justify-start">
                              <a href="/admin?view=qr-analytics">
                                <BarChart3 className="w-4 h-4 mr-2" />
                                QR Code Analytics
                              </a>
                            </Button>
                            <Button variant="outline" className="justify-start" disabled>
                              <Activity className="w-4 h-4 mr-2" />
                              User Engagement (Coming Soon)
                            </Button>
                            <Button variant="outline" className="justify-start" disabled>
                              <Calendar className="w-4 h-4 mr-2" />
                              Booking Analytics (Coming Soon)
                            </Button>
                            <Button variant="outline" className="justify-start" disabled>
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Growth Metrics (Coming Soon)
                            </Button>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="text-center space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Last updated: {new Date(analytics.timestamp).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">
                          Analytics are cached for 30 minutes for optimal performance
                        </p>
                      </div>
                    </>
                  ) : (
                    <Card>
                      <CardContent className="p-8 text-center">
                        <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-600">No analytics data available</p>
                        <Button onClick={fetchAnalytics} className="mt-4">Load Analytics</Button>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Profile View */}
              {activeView === 'profile' && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Admin Profile</h1>
                    <p className="text-gray-600 mt-1">Manage your admin account settings</p>
                  </div>

                  <Card>
                    <CardHeader>
                      <CardTitle>Account Information</CardTitle>
                      <CardDescription>Your admin account details</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        <div className="grid gap-2">
                          <label className="text-sm font-medium text-gray-700">Email</label>
                          <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                            {user?.email}
                          </div>
                        </div>
                        <div className="grid gap-2">
                          <label className="text-sm font-medium text-gray-700">Role</label>
                          <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                            Administrator
                          </div>
                        </div>
                        {user?.firstName && user?.lastName && (
                          <div className="grid gap-2">
                            <label className="text-sm font-medium text-gray-700">Name</label>
                            <div className="px-3 py-2 bg-gray-50 rounded-md text-gray-900">
                              {user.firstName} {user.lastName}
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Change Password</CardTitle>
                      <CardDescription>Update your admin password</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4">
                        <div className="grid gap-2">
                          <label htmlFor="current-password" className="text-sm font-medium text-gray-700">
                            Current Password
                          </label>
                          <input
                            id="current-password"
                            type="password"
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                          />
                        </div>
                        <div className="grid gap-2">
                          <label htmlFor="new-password" className="text-sm font-medium text-gray-700">
                            New Password
                          </label>
                          <input
                            id="new-password"
                            type="password"
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                          />
                          <p className="text-xs text-gray-500">Must be at least 8 characters long</p>
                        </div>
                        <div className="grid gap-2">
                          <label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">
                            Confirm New Password
                          </label>
                          <input
                            id="confirm-password"
                            type="password"
                            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sage-500"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                          />
                        </div>
                      </div>
                      <Button
                        onClick={handlePasswordChange}
                        className="bg-sage-600 hover:bg-sage-700"
                      >
                        Update Password
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </SidebarInset>
        </div>

        {/* Simple Admin Footer */}
        <footer className="bg-slate-900 text-white py-4 mt-auto">
          <div className="container mx-auto px-6 text-center">
            <p className="text-sm text-slate-400">
              © 2025 The Owl Co. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </SidebarProvider>
  );
}